import {NEXT_PUBLIC_BASE_URL} from 'config';
import fuzzysort from 'fuzzysort';
import {useTranslation} from 'hooks/translations/useCustomTranslation';
import {serverSideTranslations} from 'next-i18next/serverSideTranslations';
import Head from 'next/head';
import {InferGetServerSidePropsType} from 'next/types';
import React, {useMemo, useState} from 'react';

import {InputAdornment, List, ListItem, Theme, useMediaQuery} from '@mui/material';

import {getApiV1Stations} from 'lib/api/backend';
import {Station} from 'lib/api/backend.schemas';
import {SharedSeoComponent} from 'lib/api/strapi';

import {DefaultPagePropsProvider, TDefaultProps} from 'utils/dataFetching/DefaultPagePropsProvider';
import {getAllPagesStatic} from 'utils/dataFetching/fetchStaticPages';
import getDefaultPageProps from 'utils/dataFetching/getDefaultPageProps';
import {getCachedForLocale} from 'utils/dataFetching/news/getAllNews';
import {getLinkForNews} from 'utils/dataFetching/news/getLinkForNews';
import {getLocaleSlug} from 'utils/generatedStations/slugTranslations';
import {generateWrapperConfig} from 'utils/strapi-components/getComponent';
import {translationFiles} from 'utils/translations/constants';
import {redirectToLocalePath} from 'utils/translations/redirectLocale';
import {setCurrentLocale} from 'utils/translations/setCurrentLocale';

import {
  path_mapping as path_mapping_login,
  path_mapping as path_mapping_rent_for_free,
} from 'pages/specials/kostenlos-mieten';
import {path_mapping as path_mapping_tarife} from 'pages/tarife';

import {Box, Grid, NextLink, TextField, Typography} from 'components/basic-components';
import {CustomIcon} from 'components/basic-components/CustomIcon/CustomIcon';
import {DiagonalWrapper} from 'components/layout-components/DiagonalWrapper/DiagonalWrapper';
import Layout from 'components/layout-components/Layout/Layout';
import styles from 'components/section-components/specialPages/specialPages.module.scss';
import TitleWithBreadcrumb from 'components/strapi-components/TitleWithBreadcrumb/TitleWithBreadcrumb';

type TPageEntry = {
  id: string;
  title: string;
  href: string;
  path: string;
  useTranslation: boolean;
};

const customPagesMapping = [
  {pathMapping: {}, title: 'pageHome'},
  {pathMapping: path_mapping_tarife, title: 'pageRates'},
  {pathMapping: path_mapping_rent_for_free, title: 'pageRentForFree'},
  {pathMapping: path_mapping_login, title: 'pageLogin'},
  {pathMapping: {de: 'mein-starcar', en: 'my-starcar'}, title: 'pageAccount'},
];

export const getStaticProps = async ctx => {
  const {locale, locales} = ctx;
  const redirect = redirectToLocalePath({ctx, page_locale, path_mapping});
  if (redirect) return redirect;
  setCurrentLocale(locale);

  const pages = await getAllPagesStatic();

  const createCustomPageMapping = (
    id: string,
    pathMapping: {de: string; en: string} | {},
    title: string,
  ) => {
    return {
      id,
      title: title.trim(),
      href: `${NEXT_PUBLIC_BASE_URL}/${locale === 'de' ? '' : 'en/'}${pathMapping[locale] || ''}`,
      path: `/${locale === 'de' ? '' : 'en/'}${pathMapping[locale] || ''}`,
      useTranslation: true,
    };
  };

  const cmsPages: TPageEntry[] =
    pages.data
      ?.filter(page => page?.attributes?.locale === locale && !!page?.attributes?.publishedAt)
      ?.filter(page => !page?.attributes?.seo?.metaRobots?.toLocaleLowerCase()?.includes('noindex'))
      ?.map(({attributes: {fullSlug, slug, title, locale}, id}: any) => {
        return {
          id: `page-${id}`,
          title: title.trim(),
          href: `${NEXT_PUBLIC_BASE_URL}/${locale === 'de' ? '' : 'en/'}${
            fullSlug || slug ? `${fullSlug || slug}/` : ''
          }`,
          path: `/${locale === 'de' ? '' : 'en/'}${fullSlug || slug ? `${fullSlug || slug}/` : ''}`,
          useTranslation: false,
        };
      }) || [];

  const customPages: TPageEntry[] = customPagesMapping.map((page, index) => {
    return createCustomPageMapping(`customPage-${index}`, page.pathMapping, page.title);
  });

  const stationPages: TPageEntry[] =
    ((await getApiV1Stations()) as Station[])
      ?.map((station, index) => {
        return {
          id: `station-${index}-${station.id}`,
          title: `${getLocaleSlug('title', locale)} ${station.name}`,
          href: station.url || '',
          path: station.path || '',
          useTranslation: false,
        };
      })
      ?.filter(
        page =>
          page.title &&
          !cmsPages
            ?.map(page => {
              return page.title;
            })
            ?.includes(page.title),
      ) || [];

  const newsPages: TPageEntry[] =
    (await getCachedForLocale({locale: 'all'}))
      ?.filter(newsItem => !!newsItem?.attributes?.publishedAt)
      ?.filter(page => page?.attributes?.locale === locale)
      ?.filter(
        newsItem =>
          !(newsItem?.attributes?.seo as SharedSeoComponent)?.metaRobots
            ?.toLocaleLowerCase()
            ?.includes('noindex'),
      )
      ?.map((newsItem, index) => {
        return {
          id: `news-${index}-${newsItem.id}`,
          title: `${
            newsItem.attributes?.news_category?.data?.attributes?.category_name || ''
          }: ${newsItem.attributes?.title?.trim()}`,
          href: `${NEXT_PUBLIC_BASE_URL}/${newsItem?.attributes?.locale === 'de' ? '' : 'en/'}${
            getLinkForNews(newsItem, true) || ''
          }`,
          path: `/${newsItem?.attributes?.locale === 'de' ? '' : 'en/'}${
            getLinkForNews(newsItem, true) || ''
          }`,
          useTranslation: false,
        };
      }) || [];

  const pageEntries = [...cmsPages, ...customPages, ...stationPages, ...newsPages].sort((a, b) => {
    if (a.title < b.title) {
      return -1;
    }
    if (a.title > b.title) {
      return 1;
    }
    return 0;
  });

  try {
    return {
      revalidate: 60,
      props: {
        defaultProps: await getDefaultPageProps({locale, locales, seoPath: 'seitenuebersicht'}),
        pageEntries,
        ...(await serverSideTranslations(locale, translationFiles)),
      }, // will be passed to the page component as props
    };
  } catch (err) {
    return {
      notFound: true,
      revalidate: 30,
      props: {
        defaultProps: await getDefaultPageProps({locale, locales}),
      },
    };
  }
};

// will be able to inferServerSide PRops with next 12.3.2
// https://github.com/vercel/next.js/pull/40635
const DynamicPage: InferGetServerSidePropsType<any> = ({
  defaultProps,
  pageEntries,
}: {
  defaultProps: TDefaultProps;
  pageEntries: TPageEntry[];
}) => {
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('xl'));
  const isTablet = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const {t} = useTranslation('common');
  const wrapperDefaultConfig = generateWrapperConfig();
  const [searchTerm, setSearchTerm] = useState('');

  const sortPages = (pageEntries: TPageEntry[]) => {
    const grouped = pageEntries.reduce((acc, page) => {
      let firstChar = page.title.toLowerCase().charAt(0);
      if (/^\d$/.test(firstChar)) firstChar = '0-9';
      if (!acc[firstChar]) {
        acc[firstChar] = [];
      }
      acc[firstChar].push(page);
      return acc;
    }, {} as {[char: string]: TPageEntry[]});

    return Object.keys(grouped)
      .sort()
      .reduce((acc, key) => {
        acc[key] = grouped[key];
        return acc;
      }, {} as {[char: string]: TPageEntry[]});
  };

  const filtered = useMemo(() => {
    const searchTermClean = searchTerm.trim().toLowerCase();

    if (searchTermClean.length === 0) return sortPages(pageEntries);

    if (searchTermClean.length === 1)
      return sortPages(
        pageEntries.filter(page => {
          const titleMatch = page.title.toLowerCase().charAt(0).includes(searchTermClean);
          const pathMatch = page.path.toLowerCase().charAt(0).includes(searchTermClean);
          return titleMatch || pathMatch;
        }),
      );

    const fuzzySearchItems = fuzzysort
      .go(searchTermClean, pageEntries, {
        keys: ['title', 'path'],
        all: true,
      })
      ?.map(result => result.obj)
      ?.sort((a, b) => {
        if (a.title < b.title) {
          return -1;
        }
        if (a.title > b.title) {
          return 1;
        }
        return 0;
      });
    return fuzzySearchItems?.length > 0 ? sortPages(fuzzySearchItems) : sortPages(pageEntries);
  }, [searchTerm, pageEntries]);

  function splitArrayIntoParts(array: any[], parts: number) {
    const totalLength = array.length;
    const partSize = Math.ceil(totalLength / parts);

    const result = [];

    for (let i = 0; i < totalLength; i += partSize) {
      result.push(array.slice(i, i + partSize));
    }

    return result;
  }

  return (
    <DefaultPagePropsProvider defaultProps={defaultProps}>
      <Layout
        translatedSlugs={[
          {slug: 'seitenuebersicht', locale: 'de'},
          {slug: 'page-overview', locale: 'en'},
        ]}
      >
        <div style={{zIndex: 1}}>
          <Head>
            <title>{t('pageOverview')}</title>
          </Head>

          <DiagonalWrapper
            noPadding
            config={{...wrapperDefaultConfig, diagonal: {bottom: true, top: false}}}
            background={'yellow'}
          >
            <TitleWithBreadcrumb
              title={t('pageOverview')}
              titleLevel="h1"
              breadcrumb={[{linkText: t('pageOverview')}]}
              textContent={t('pageOverviewHint')}
            ></TitleWithBreadcrumb>

            <Grid xxs={12} pb={'40px'}>
              <TextField
                className={styles.searchField}
                sx={{
                  '& .MuiFilledInput-root': {
                    outline: 'none',
                    height: '50px',
                    '& .MuiInputAdornment-root': {margin: '0 !important'},
                    '& .MuiInputAdornment-root+.MuiFilledInput-input': {
                      padding: '13px 10px 13px 0',
                    },
                  },
                }}
                variant="filled"
                id="standard-search"
                placeholder={t('keyword')}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CustomIcon name="magnifyingglass" className={styles.icon} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      {searchTerm ? (
                        <CustomIcon
                          onClick={() => setSearchTerm('')}
                          name="close"
                          className={styles.clearIcon}
                        />
                      ) : null}
                    </InputAdornment>
                  ),
                }}
                type="text"
                value={searchTerm}
                onChange={e => {
                  setSearchTerm(e.target.value);
                }}
                onKeyDown={e => {
                  if (e.key === 'Escape') {
                    setSearchTerm('');
                  }
                }}
              />
            </Grid>
          </DiagonalWrapper>

          <DiagonalWrapper
            noPadding
            config={{...wrapperDefaultConfig, diagonal: {bottom: true, top: true}}}
            background={'white'}
          >
            <Box sx={{margin: '50px 0'}}>
              <Grid container>
                {Object.entries(filtered)?.map(([key, pageEntries], index) => {
                  const splitParts =
                    pageEntries.length >= 6 ? (isDesktop ? 3 : isTablet ? 2 : 1) : 1;
                  const pageEntriesSplitted = splitArrayIntoParts(pageEntries, splitParts);
                  return (
                    <Grid xxs={12} key={`${index}-${key}`}>
                      <Typography
                        variant="h2"
                        sx={{
                          fontSize: {xxs: '24px', md: '40px'},
                          lineHeight: {xxs: '32px', md: '50px', margin: '12px 0'},
                        }}
                      >
                        {key.includes('zStations') ? t(key) : key.toUpperCase()}
                      </Typography>
                      <List sx={{listStyleType: 'disc', paddingLeft: '20px'}}>
                        <Box
                          sx={{
                            display: 'grid',
                            columnGap: '2%',
                            gridTemplateColumns: `${
                              splitParts > 1 ? `repeat(${splitParts}, 1fr)` : 'none'
                            }`,
                          }}
                        >
                          {pageEntriesSplitted.map((pageEntries, index) => (
                            <Box key={index}>
                              {pageEntries?.map(({id, title, useTranslation, path}: TPageEntry) => {
                                return (
                                  <ListItem
                                    key={id}
                                    sx={{display: 'list-item', padding: '0 5px 0 0'}}
                                  >
                                    <Typography className={styles.footerLinkStyles}>
                                      <NextLink href={path} target={'_self'}>
                                        {useTranslation ? t(title) : title}
                                      </NextLink>
                                    </Typography>
                                  </ListItem>
                                );
                              })}
                            </Box>
                          ))}
                        </Box>
                      </List>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          </DiagonalWrapper>
        </div>
      </Layout>
    </DefaultPagePropsProvider>
  );
};

export default DynamicPage;

export const path_mapping = {de: 'seitenuebersicht', en: 'page-overview'};
export const page_locale = 'de';
